// Admin state management utilities

import { apiFetch } from "@/lib/api";
import { STATUS_STORAGE_URL, STATUS_UPDATE_API_URL, getBackendUrl, UserRole } from "@/config/admin";

const ADMIN_LOGGED_IN_KEY = "nfc_admin_logged_in";
const ADMIN_ROLE_KEY = "nfc_admin_role";

/** Token key used by AuthContext; kept in sync for isAdminLoggedIn / logoutAdmin */
export const AUTH_TOKEN_KEY = "scja_access_token";

// Cache for status to avoid too many requests
let statusCache: { status: boolean; timestamp: number } | null = null;
const CACHE_DURATION = 5000; // 5 seconds cache

// Get admin status from shared storage (JSON file)
export const getAdminStatus = async (): Promise<boolean> => {
  try {
    // Check cache first
    if (statusCache && Date.now() - statusCache.timestamp < CACHE_DURATION) {
      return statusCache.status;
    }

    const response = await apiFetch(STATUS_STORAGE_URL(), {
      cache: "no-cache",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`Failed to fetch status from ${STATUS_STORAGE_URL()}: ${response.status} ${response.statusText}`);
      // Fallback to localStorage if API doesn't work
      const localStatus = localStorage.getItem("nfc_admin_status");
      return localStatus === "active";
    }

    const data = await response.json();
    const status = (data?.data ?? data)?.status;
    const isActive = status === "active";
    
    // Update cache
    statusCache = { status: isActive, timestamp: Date.now() };

    if (import.meta.env.DEV) {
      console.log(`✅ Status fetched from backend: ${isActive ? 'active' : 'inactive'}`);
    }

    return isActive;
  } catch (error) {
    if (import.meta.env.DEV) console.error(`❌ Error fetching status from ${STATUS_STORAGE_URL()}:`, error);
    // Fallback to localStorage
    const localStatus = localStorage.getItem("nfc_admin_status");
    return localStatus === "active";
  }
};

// Synchronous version for immediate use (uses cache or localStorage fallback)
export const getAdminStatusSync = (): boolean => {
  if (statusCache) {
    return statusCache.status;
  }
  const localStatus = localStorage.getItem("nfc_admin_status");
  return localStatus === "active";
};

// Set admin status - updates via API endpoint. Throws when API fails so UI can revert and show error.
export const setAdminStatus = async (isActive: boolean): Promise<void> => {
  const statusUpdateUrl = STATUS_UPDATE_API_URL();
  const token = getAdminToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await apiFetch(statusUpdateUrl, {
    method: "POST",
    headers,
    body: {
      status: isActive ? "active" : "inactive",
    },
  });

  if (!response.ok) {
    const text = await response.text();
    let msg = `Failed to update status (${response.status})`;
    try {
      const parsed = text ? JSON.parse(text) : {};
      msg = parsed?.message ?? parsed?.error ?? msg;
    } catch {
      // use default msg
    }
    throw new Error(msg);
  }

  // Success: update localStorage and cache
  localStorage.setItem("nfc_admin_status", isActive ? "active" : "inactive");
  statusCache = { status: isActive, timestamp: Date.now() };
};

export const getAdminToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const isAdminLoggedIn = (): boolean => {
  return !!getAdminToken();
};

export const setAdminLoggedIn = (loggedIn: boolean, role?: UserRole): void => {
  localStorage.setItem(ADMIN_LOGGED_IN_KEY, loggedIn ? "true" : "false");
  if (role) {
    localStorage.setItem(ADMIN_ROLE_KEY, role);
  }
};

export const getAdminRole = (): UserRole | null => {
  const role = localStorage.getItem(ADMIN_ROLE_KEY);
  if (role === "admin" || role === "superadmin" || role === "manager" || role === "tech_admin") {
    return role;
  }
  return null;
};

export const isSuperAdmin = (): boolean => {
  return getAdminRole() === "superadmin";
};

export const logoutAdmin = (): void => {
  localStorage.removeItem(ADMIN_LOGGED_IN_KEY);
  localStorage.removeItem(ADMIN_ROLE_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
};
